/**
 * MPP Setups from Memory geometry — PullBack, Breakout, Bounce, Box, Touch,
 * Reversal, Headwind, Flow.
 *
 * There is no book formula for these names. They are how price INTERACTS with
 * live MPP - Auto Trendlines lines (the Memory engine, same as the chart study),
 * analogous to the scanner's TL bounce/break columns:
 *
 *   Touch     wick within tol of a live line
 *   Bounce    kiss (no pierce) + close back on the holding side
 *   Reversal  wick THROUGH the touch band + close reclaimed the holding side
 *   Breakout  Memory's own close-through (brkB) in the last 3 bars
 *   PullBack  throwback to a recently broken line, close still on the
 *             breakout side (broken resistance → support, and the mirror)
 *   Box       bar contained between a live support AND a live resistance
 *   Headwind  nearest live resistance within 1.5×ATR above (−1) or live
 *             support within 1.5×ATR below (+1)
 *   Flow      last 3 bars hugging an up-sloping live support (+1) or a
 *             down-sloping live resistance (−1)
 *
 * One Memory walk per OHLCV array (memoised on `closes`). Scanner columns
 * read lastFinite of each series; the chart + Indicator picker share the
 * same defs.
 */
import type { OHLCV, IndicatorParams } from "../types";
import {
  lineVal,
  walkCueMemory,
  type CueMemoryMachine,
} from "@/lib/indicators/cueMemory";

const WARMUP = 300;
const BREAK_BARS = 3;
const PULLBACK_BARS = 12;
const FLOW_BARS = 3;
const HEADWIND_ATR = 1.5;
const TOUCH_PCT = 0.006;

export type MemorySetupPack = {
  pullback: (number | null)[];
  breakout: (number | null)[];
  bounce: (number | null)[];
  box: (number | null)[];
  touch: (number | null)[];
  reversal: (number | null)[];
  headwind: (number | null)[];
  flow: (number | null)[];
};

const emptyPack = (n: number): MemorySetupPack => ({
  pullback: new Array<number | null>(n).fill(null),
  breakout: new Array<number | null>(n).fill(null),
  bounce: new Array<number | null>(n).fill(null),
  box: new Array<number | null>(n).fill(null),
  touch: new Array<number | null>(n).fill(null),
  reversal: new Array<number | null>(n).fill(null),
  headwind: new Array<number | null>(n).fill(null),
  flow: new Array<number | null>(n).fill(null),
});

function atrAt(H: number[], L: number[], Cl: number[], i: number, len = 14): number {
  if (i < 1) return 0;
  const start = Math.max(1, i - len + 1);
  let s = 0;
  let k = 0;
  for (let j = start; j <= i; j++) {
    const tr = Math.max(
      H[j]! - L[j]!,
      Math.abs(H[j]! - Cl[j - 1]!),
      Math.abs(L[j]! - Cl[j - 1]!),
    );
    s += tr;
    k++;
  }
  return k > 0 ? s / k : 0;
}

function tol(y: number, atr: number): number {
  return Math.max(0.5 * atr, TOUCH_PCT * Math.abs(y));
}

function yOf(m: CueMemoryMachine, side: 0 | 1, L: number[], H: number[], x: number): number {
  const p1 = side === 0 ? L[m.orig]! : H[m.orig]!;
  const p2 = side === 0 ? L[m.dest]! : H[m.dest]!;
  return lineVal(m.orig, p1, m.dest, p2, x);
}

function slopeOf(m: CueMemoryMachine, side: 0 | 1, L: number[], H: number[]): number {
  if (m.dest === m.orig) return 0;
  const p1 = side === 0 ? L[m.orig]! : H[m.orig]!;
  const p2 = side === 0 ? L[m.dest]! : H[m.dest]!;
  return (p2 - p1) / (m.dest - m.orig);
}

/** Pure classifier — exported so the check can pin geometry without 300 bars. */
export function classifyMemorySetups(input: {
  i: number;
  high: number;
  low: number;
  close: number;
  atr: number;
  H: number[];
  L: number[];
  machines: readonly CueMemoryMachine[];
  warmupBars: number;
}): {
  pullback: number | null;
  breakout: number | null;
  bounce: number | null;
  box: number | null;
  touch: number | null;
  reversal: number | null;
  headwind: number | null;
  flow: number | null;
} {
  const { i, high, low, close, atr, H, L, machines, warmupBars } = input;
  const none = {
    pullback: null as number | null,
    breakout: null as number | null,
    bounce: null as number | null,
    box: null as number | null,
    touch: null as number | null,
    reversal: null as number | null,
    headwind: null as number | null,
    flow: null as number | null,
  };
  if (i + 1 < warmupBars) return none;

  type Live = { m: CueMemoryMachine; side: 0 | 1; y: number; sl: number };
  const live: Live[] = [];
  let breakout: number | null = null;
  let pullback: number | null = null;

  for (let mm = 0; mm < machines.length; mm++) {
    const m = machines[mm]!;
    if (m.orig < 0 || m.drawB + 1 < warmupBars) continue;
    const side = (mm % 2) as 0 | 1;
    const y = yOf(m, side, L, H, i);
    if (!(y > 0)) continue;
    const t = tol(y, atr);

    if (m.brkB < 0) {
      live.push({ m, side, y, sl: slopeOf(m, side, L, H) });
    } else {
      const ago = i - m.brkB;
      if (ago >= 0 && ago < BREAK_BARS) {
        const sig = side === 0 ? -1 : 1;
        if (breakout == null) breakout = sig;
      }
      if (ago >= 1 && ago <= PULLBACK_BARS) {
        if (side === 1 && close > y && low <= y + t && low >= y - t) {
          pullback = 1;
        } else if (side === 0 && close < y && high >= y - t && high <= y + t) {
          pullback = pullback === 1 ? 1 : -1;
        }
      }
    }
  }

  let touch: number | null = null;
  let bounce: number | null = null;
  let reversal: number | null = null;
  let nearestSup: Live | null = null;
  let nearestRes: Live | null = null;

  for (const ln of live) {
    const t = tol(ln.y, atr);
    const distSup = close - ln.y;
    const distRes = ln.y - close;
    if (ln.side === 0) {
      if (!nearestSup || Math.abs(distSup) < Math.abs(close - nearestSup.y)) nearestSup = ln;
      if (low <= ln.y + t && low >= ln.y - t) touch = touch === -1 ? touch : 1;
      if (low < ln.y - t && close >= ln.y) reversal = reversal === -1 ? reversal : 1;
      else if (low <= ln.y + t && low >= ln.y - t * 0.25 && close >= ln.y) {
        bounce = bounce === -1 ? bounce : 1;
      }
    } else {
      if (!nearestRes || Math.abs(distRes) < Math.abs(nearestRes.y - close)) nearestRes = ln;
      if (high >= ln.y - t && high <= ln.y + t) touch = -1;
      if (high > ln.y + t && close <= ln.y) reversal = -1;
      else if (high >= ln.y - t && high <= ln.y + t * 0.25 && close <= ln.y) {
        bounce = -1;
      }
    }
  }

  let box: number | null = null;
  if (nearestSup && nearestRes && nearestRes.y > nearestSup.y) {
    const lo = nearestSup.y - tol(nearestSup.y, atr);
    const hi = nearestRes.y + tol(nearestRes.y, atr);
    const width = nearestRes.y - nearestSup.y;
    if (low >= lo && high <= hi && width >= 0.5 * atr && width <= 0.15 * close) {
      box = 1;
    }
  }

  let headwind: number | null = null;
  const hw = HEADWIND_ATR * atr;
  if (nearestRes && close < nearestRes.y && nearestRes.y - close <= hw) headwind = -1;
  else if (nearestSup && close > nearestSup.y && close - nearestSup.y <= hw) headwind = 1;

  const hugs = (ln: Live, side: 0 | 1): boolean => {
    if (i < FLOW_BARS - 1) return false;
    for (let k = 0; k < FLOW_BARS; k++) {
      const j = i - k;
      const yj = yOf(ln.m, side, L, H, j);
      const tj = tol(yj, atr);
      if (side === 0) {
        if (L[j]! > yj + tj || L[j]! < yj - tj) return false;
      } else if (H[j]! < yj - tj || H[j]! > yj + tj) {
        return false;
      }
    }
    return true;
  };

  let flow: number | null = null;
  if (nearestSup && nearestSup.sl > 0 && close >= nearestSup.y && hugs(nearestSup, 0)) flow = 1;
  else if (nearestRes && nearestRes.sl < 0 && close <= nearestRes.y && hugs(nearestRes, 1)) flow = -1;

  return {
    pullback: pullback ?? 0,
    breakout: breakout ?? 0,
    bounce: bounce ?? 0,
    box: box ?? 0,
    touch: touch ?? 0,
    reversal: reversal ?? 0,
    headwind: headwind ?? 0,
    flow: flow ?? 0,
  };
}

let cache: { closes: number[]; n: number; pack: MemorySetupPack } | null = null;

export function computeMemorySetups(data: OHLCV, _params: IndicatorParams = {}): MemorySetupPack {
  void _params;
  const n = data.closes.length;
  if (n === 0) return emptyPack(0);
  if (cache && cache.closes === data.closes && cache.n === n) return cache.pack;

  const highs = data.highs ?? data.closes;
  const lows = data.lows ?? data.closes;
  const H = new Array<number>(n);
  const L = new Array<number>(n);
  const Cl = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    H[i] = Math.round(highs[i]! * 100) / 100;
    L[i] = Math.round(lows[i]! * 100) / 100;
    Cl[i] = Math.round(data.closes[i]! * 100) / 100;
  }

  const pack = emptyPack(n);
  if (n < 20) {
    cache = { closes: data.closes, n, pack };
    return pack;
  }

  walkCueMemory(H, L, Cl, WARMUP, (i, machines) => {
    const flags = classifyMemorySetups({
      i,
      high: H[i]!,
      low: L[i]!,
      close: Cl[i]!,
      atr: atrAt(H, L, Cl, i),
      H,
      L,
      machines,
      warmupBars: WARMUP,
    });
    pack.pullback[i] = flags.pullback;
    pack.breakout[i] = flags.breakout;
    pack.bounce[i] = flags.bounce;
    pack.box[i] = flags.box;
    pack.touch[i] = flags.touch;
    pack.reversal[i] = flags.reversal;
    pack.headwind[i] = flags.headwind;
    pack.flow[i] = flags.flow;
  });

  cache = { closes: data.closes, n, pack };
  return pack;
}
